import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { IApplicationFiltersExtended, IFilterOption, IPriceRange } from '../../shared/shared.definitions';
import { ApiService } from '../../shared/services/api.service';

@Injectable()
export class CollectionFilterService {
  private readonly defaultFilters: IApplicationFiltersExtended = {
    search: '',
    destinations: [],
    settlements: [],
    serviceTypes: [],
    summerServices: [],
    acceptsCardType: [],
    accommodationType: [],
    accommodationClass: [],
    accommodationFeature: [],
    accommodationService: [],
    campaignOffers: [],
    accommodationNature: {
      accommodationForFamilies: false,
      accommodationForCouples: false,
      isAccessible: false,
    },
    paymentMethod: [],
    equipment: {
      accommodationForFamilies: false,
      accommodationForCouples: false,
      isAccessible: false,
      acceptsSzepCard: false,
      has24HourReception: false,
      hasRestaurant: false,
      idealForCouples: false,
      petFriendly: false,
      freeParking: false,
      paidParking: false,
      freeWifi: false,
      hasElevator: false,
      hasWellnessServices: false,
      hasPrivateBeach: false,
      hasOutdoorPool: false,
      hasIndoorPool: false,
      hasJacuzzi: false,
      hasSauna: false,
      hasInfraSauna: false,
      hasFitness: false,
      familyFriendly: false,
      hasOutdoorPlayground: false,
      hasIndoorPlayArea: false,
      hasKidsPool: false,
      hasCrib: false,
      hasChildcare: false,
    },
    suburb: [],
    city: [],
    priceRange: null,
    discountPercent: null,
  };

  private readonly filtersSubject$ = new BehaviorSubject<IApplicationFiltersExtended>(this.defaultFilters);
  private readonly visibleFiltersSubject$ = new BehaviorSubject<Record<string, boolean>>({});

  filters$ = this.filtersSubject$.asObservable(); // Observable for components to subscribe to
  visibleFilters$ = this.visibleFiltersSubject$.asObservable();

  constructor(private readonly apiService: ApiService) {}

  fetchFilterOptions(type: string, uuid: string, componentUuid: string): void {
    this.apiService.getApplicationsCollection$(type, uuid, componentUuid, 0, 1).subscribe((res) => {
      this.updateVisibleFiltersFromMeta(res?.meta);
      const currentFilters = this.getFilters();
      const updatedFilters: IApplicationFiltersExtended = { ...currentFilters };
      let shouldUpdate = false;

      if (res?.meta?.filterOptions?.priceRange) {
        const minPrice = Number(res.meta.filterOptions.priceRange.min);
        const maxPrice = Number(res.meta.filterOptions.priceRange.max);
        if (Number.isFinite(minPrice) && Number.isFinite(maxPrice) && minPrice < maxPrice) {
          const step = 1000;
          const normalizedMin = Math.max(0, Math.floor(minPrice / step) * step);
          const normalizedMax = Math.ceil(maxPrice / step) * step;

          if (normalizedMin < normalizedMax) {
            updatedFilters.priceRange = {
              start: normalizedMin,
              end: normalizedMax,
              min: normalizedMin,
              max: normalizedMax,
            };
            shouldUpdate = true;
          }
        }
      }

      const filterOptions = res?.meta?.filterOptions;

      if (filterOptions?.accommodationFeature) {
        updatedFilters.accommodationFeature = this.mapFilterOptions(
          filterOptions.accommodationFeature,
          currentFilters.accommodationFeature
        );
        shouldUpdate = true;
      }

      if (filterOptions?.accommodationService) {
        updatedFilters.accommodationService = this.mapFilterOptions(
          filterOptions.accommodationService,
          currentFilters.accommodationService
        );
        shouldUpdate = true;
      }

      if (filterOptions?.campaignOffers) {
        updatedFilters.campaignOffers = this.mapFilterOptions(filterOptions.campaignOffers, currentFilters.campaignOffers);
        shouldUpdate = true;
      }

      if (filterOptions?.discountPercent) {
        updatedFilters.discountPercent = this.mapRangeOption(filterOptions.discountPercent, currentFilters.discountPercent, {
          min: 0,
          max: 100,
        });
        shouldUpdate = true;
      }

      if (shouldUpdate) {
        this.updateFilters(updatedFilters);
      }
    });
  }

  private mapFilterOptions(rawOptions: Record<string, string>, previousOptions?: IFilterOption[]): IFilterOption[] {
    if (!rawOptions) {
      return [];
    }

    const checkedMap = new Map<string, boolean>((previousOptions ?? []).map((option) => [String(option.id), option.checked]));

    return Object.keys(rawOptions).map((key) => {
      const numericId = Number(key);
      const idValue = Number.isNaN(numericId) ? (key as unknown as number) : numericId;
      const normalizedKey = String(key);

      return {
        id: idValue,
        text: rawOptions[key],
        checked: checkedMap.get(normalizedKey) ?? checkedMap.get(String(idValue)) ?? false,
      };
    });
  }

  getFilters(): IApplicationFiltersExtended {
    return this.filtersSubject$.value;
  }

  getVisibleFilters(): Record<string, boolean> {
    return this.visibleFiltersSubject$.value;
  }

  updateFilters(filters: IApplicationFiltersExtended): void {
    this.filtersSubject$.next(filters);
  }

  updateFilter<T>(key: keyof IApplicationFiltersExtended, value: T): void {
    const currentFilters = this.getFilters();

    const updatedFilters: IApplicationFiltersExtended = {
      ...currentFilters,
      [key]: value,
    };

    this.filtersSubject$.next(updatedFilters);
  }

  getCurrentFilters(): IApplicationFiltersExtended {
    return this.filtersSubject$.value;
  }

  hasActiveFilters(): boolean {
    const currentFilters = this.getFilters();

    for (const key in currentFilters) {
      const filterOptions = currentFilters[key];

      if (Array.isArray(filterOptions)) {
        if ((filterOptions as IFilterOption[]).some((filterOption) => filterOption.checked)) {
          return true;
        }
      } else if ((key === 'priceRange' || key === 'discountPercent') && filterOptions) {
        const range = filterOptions as IPriceRange;
        const { min, max, start, end } = range;
        if (min !== undefined && max !== undefined) {
          const effectiveStart = start ?? min;
          const effectiveEnd = end ?? max;
          if (effectiveStart > min || effectiveEnd < max) {
            return true;
          }
        }
      } else if (typeof filterOptions === 'string' && filterOptions.trim() !== '') {
        return true;
      }
    }

    return false;
  }

  resetFilters(): void {
    const currentFilters = this.getFilters();
    const updatedFilters: IApplicationFiltersExtended = Object.assign({}, this.defaultFilters);

    for (const key in currentFilters) {
      if (Array.isArray(currentFilters[key])) {
        updatedFilters[key] = (currentFilters[key] as IFilterOption[]).map((filterOption: IFilterOption) => ({
          ...filterOption,
          checked: false,
        }));
      } else if (key === 'accommodationNature') {
        updatedFilters.accommodationNature = { ...this.defaultFilters.accommodationNature };
      } else if (key === 'equipment') {
        updatedFilters.equipment = { ...this.defaultFilters.equipment };
      } else if (key === 'priceRange' && currentFilters.priceRange) {
        updatedFilters.priceRange = {
          ...currentFilters.priceRange,
          start: currentFilters.priceRange.min,
          end: currentFilters.priceRange.max,
        };
      } else if (key === 'discountPercent' && currentFilters.discountPercent) {
        updatedFilters.discountPercent = {
          ...currentFilters.discountPercent,
          start: currentFilters.discountPercent.min,
          end: currentFilters.discountPercent.max,
        };
      }
    }

    this.filtersSubject$.next(updatedFilters);
  }

  resetFilter(key: keyof IApplicationFiltersExtended): void {
    const currentFilters = this.getFilters();
    const target = currentFilters[key];

    if (!Array.isArray(target)) {
      return;
    }

    const updatedFilters: IApplicationFiltersExtended = {
      ...currentFilters,
      [key]: target.map((filterOption: IFilterOption) => ({
        ...filterOption,
        checked: false,
      })),
    };

    this.filtersSubject$.next(updatedFilters);
  }

  updateVisibleFiltersFromMeta(meta: any): void {
    const filterOptions = meta?.filterOptions ?? {};

    const visibleFilters: Record<string, boolean> = {
      search: filterOptions.keywordSearch !== undefined,
      discountPercent: filterOptions.discountPercent !== undefined,
      suburb: this.hasMoreThanOneOption(filterOptions.suburb),
      city: this.hasMoreThanOneOption(filterOptions.city),
      destinations: this.hasMoreThanOneOption(filterOptions.destinationTag_filter),
      serviceTypes: this.hasMoreThanOneOption(filterOptions.serviceTypes_filter),
      summerServices: this.hasMoreThanOneOption(filterOptions.summerServices),
      acceptsCardType: this.hasMoreThanOneOption(filterOptions.acceptsCardType),
      accommodationType: this.hasMoreThanOneOption(filterOptions.accommodationType),
      accommodationClass: this.hasMoreThanOneOption(filterOptions.accommodationClass),
      accommodationFeature: this.hasMoreThanOneOption(filterOptions.accommodationFeature),
      accommodationService: this.hasMoreThanOneOption(filterOptions.accommodationService),
      campaignOffers: this.hasMoreThanOneOption(filterOptions.campaignOffers),
      paymentMethod: this.hasMoreThanOneOption(filterOptions.paymentMethod),
      accommodationNature: filterOptions.accommodationStyle !== undefined,
      equipment: filterOptions.equipment !== undefined,
      priceRange: this.isValidPriceRange({ min: filterOptions?.priceRange?.min, max: filterOptions?.priceRange?.max }),
    };

    this.visibleFiltersSubject$.next(visibleFilters);
  }

  private mapRangeOption(
    rangeOption: any,
    previousRange: IPriceRange | null | undefined,
    fallback?: { min: number; max: number }
  ): IPriceRange {
    const defaultMin = fallback?.min ?? previousRange?.min ?? 0;
    const defaultMax = fallback?.max ?? previousRange?.max ?? defaultMin;
    const min = this.parseNumber(rangeOption?.min, defaultMin);
    const max = this.parseNumber(rangeOption?.max, defaultMax);
    const startDefault = previousRange?.start ?? min;
    const endDefault = previousRange?.end ?? max;
    const start = this.parseNumber(rangeOption?.start, startDefault);
    const end = this.parseNumber(rangeOption?.end, endDefault);

    const clampedStart = Math.min(Math.max(start, min), max);
    const clampedEnd = Math.min(Math.max(end, min), max);
    const normalizedStart = Math.min(clampedStart, clampedEnd);
    const normalizedEnd = Math.max(clampedStart, clampedEnd);

    return {
      min,
      max,
      start: normalizedStart,
      end: normalizedEnd,
    };
  }

  private hasMoreThanOneOption(value: unknown): boolean {
    if (!value || typeof value !== 'object') {
      return false;
    }

    return Object.keys(value as Record<string, unknown>).length > 1;
  }

  private isValidPriceRange(range?: { min?: unknown; max?: unknown }): boolean {
    const min = this.parseNumber(range?.min, Number.NaN);
    const max = this.parseNumber(range?.max, Number.NaN);

    return Number.isFinite(min) && Number.isFinite(max) && min < max;
  }

  private parseNumber(value: unknown, fallback: number): number {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : fallback;
  }
}
